import { hashKey, type QueryClient } from '@tanstack/react-query'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { PaymentStatus } from './billing.config'

// Refreshing the billing cache after a payment.
//
// The gateway credits the balance (and syncs a newly-vaulted card into the
// shared cards table) from its ASYNC payment.captured webhook, which lands AFTER
// the browser's verify call returns. A single immediate invalidateQueries
// therefore refetches and re-caches the PRE-payment values, and nothing refetches
// again (refetchOnWindowFocus is false) — so the customer sees a success toast
// next to an unchanged balance until they reload the page.
//
// The fix is to poll until the value ACTUALLY changes, bounded: snapshot the
// cache before the first invalidate (at that moment it still holds the
// pre-payment value, which is exactly the baseline we need), then invalidate on
// an interval until the refetched value has moved or the budget is spent.
//
// Deliberately kept free of billingService / logger / neo imports so the whole
// module stays pure and unit-testable with a local `new QueryClient()`. The
// QueryClient is a PARAMETER, never the shared rq-setup singleton: five test
// files mock.module that singleton with a 2-3 method stub, and those mocks leak
// process-globally (AGENTS.md failure mode 1), so calling a method they don't
// expose would kill whichever test file runs next.

export const BILLING_REFRESH_POLL_INTERVAL_MS = 2500
export const BILLING_REFRESH_MAX_POLL_ATTEMPTS = 8
// The gateway told us the payment is not captured yet (Razorpay captured:false,
// Cashfree PENDING/ACTIVE). A netbanking bank confirmation genuinely takes tens
// of seconds, so that case gets a longer window rather than no window at all.
export const BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS = 16

export type BillingRefreshTarget = 'balance' | 'cards' | 'transactions'
export type BillingRefreshOutcome = 'updated' | 'timed_out' | 'no_baseline' | 'not_tracked'

export interface BillingRefreshOptions {
  // Which values must actually change before we stop early. Every key is
  // invalidated on every attempt regardless — this governs only the stop
  // condition, so tracking a value that was never going to move (e.g. cards on a
  // recharge that saves nothing) just burns the budget.
  targets?: BillingRefreshTarget[]
  // Injectable so tests run the real loop on real timers in milliseconds — this
  // repo has no fake timers and bun test has no jest-style timer control.
  intervalMs?: number
  maxAttempts?: number
}

export interface BillingRefreshResult {
  balance: BillingRefreshOutcome
  cards: BillingRefreshOutcome
  transactions: BillingRefreshOutcome
  attempts: number
}

interface BalanceEntry {
  amount?: number
}

interface CardEntry {
  card_mapping_id: number
}

interface TransactionsEntry {
  payment_logs?: Array<{ id?: number; payment_status?: string }>
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

// Read every cached balance entry under the balanceAll() PREFIX, keyed by its own
// query hash. There is one entry per conversionRate, and getQueryData cannot be
// used here: it hashes for an exact lookup, so getQueryData(balanceAll(id)) is
// always undefined — balanceAll is an invalidation prefix, never a real key.
export const readBalanceAmounts = (client: QueryClient, accountId: number): Map<string, number> => {
  const amounts = new Map<string, number>()
  for (const [queryKey, data] of client.getQueriesData<BalanceEntry>({
    queryKey: billingQueryKeys().balanceAll(accountId),
  })) {
    if (typeof data?.amount === 'number') amounts.set(hashKey(queryKey), data.amount)
  }
  return amounts
}

// undefined (NOT []) when the cards key has never been cached — that distinction
// is what stops "the first fetch arrived" being mistaken for "a card was added".
export const readCardIds = (client: QueryClient, accountId: number): number[] | undefined => {
  const cards = client.getQueryData<CardEntry[]>(billingQueryKeys().cards(accountId))
  return cards?.map((card) => card.card_mapping_id)
}

// The Recent Receipts / Payments row renders `payment_status` straight from this
// data, and a fresh payment lands as PENDING before the webhook flips it. Read
// across the PREFIX because the key carries conversionRate + pagination, exactly
// like readBalanceAmounts — an exact-key read would miss every page but the first.
//
// undefined (NOT an empty map) when nothing is cached, so "the first fetch arrived"
// can't be mistaken for "a row settled" (same convention as readCardIds).
export const readTransactionStatuses = (
  client: QueryClient,
  accountId: number,
): Map<number, string> | undefined => {
  let seen = false
  const statuses = new Map<number, string>()
  for (const [, data] of client.getQueriesData<TransactionsEntry>({
    queryKey: billingQueryKeys().transactionsAll(accountId),
  })) {
    if (!data?.payment_logs) continue
    seen = true
    for (const log of data.payment_logs) {
      if (typeof log?.id === 'number' && typeof log?.payment_status === 'string') {
        statuses.set(log.id, log.payment_status)
      }
    }
  }
  return seen ? statuses : undefined
}

// Whether anything is currently MOUNTED on this key. refetchType is 'active', so an
// inactive query is never refetched by our invalidate — its cache cannot change, and
// a stop condition that waits for it to change can only time out.
const hasActiveQuery = (client: QueryClient, queryKey: readonly unknown[]): boolean =>
  client.getQueryCache().findAll({ queryKey: [...queryKey], type: 'active' }).length > 0

// Settled means PROGRESS WAS OBSERVED: a row that is terminal now was either absent
// or pending at baseline. Nothing weaker qualifies.
//
// In particular "no row is pending" is NOT evidence. A returning customer's cache
// holds only terminal rows from earlier payments, and in the normal ordering the
// balance updates BEFORE the transactions query exposes the new row — so an
// unchanged cache is the state every run STARTS in. An earlier version treated it as
// settled and the poll stopped on attempt 1 with the receipt list stale (caught in
// review).
//
// The cost of requiring a transition: if the webhook landed before the baseline was
// even captured, the row is already terminal in it, no transition is observable, and
// the run spends its full budget and reports timed_out. That is silent extra
// refreshes against a UI that is already correct — the fail-open direction this
// module prefers, and the same trade hasBalanceIncreased makes with `>` over `!==`.
export const hasTransactionSettled = (
  baseline: ReadonlyMap<number, string>,
  current: ReadonlyMap<number, string>,
): boolean => {
  for (const [id, status] of current) {
    if (status === PaymentStatus.PENDING) continue
    const before = baseline.get(id)
    // A row that is terminal NOW and was either absent or pending at baseline is
    // this payment landing. Nothing else counts.
    if (before === undefined || before === PaymentStatus.PENDING) return true
  }
  return false
}

// Increased, not merely different. A credit is monotonically upward, so `>` fails
// OPEN: an unrelated usage debit landing inside the window just burns the
// remaining budget. `!==` would fail CLOSED — that debit satisfies it, we stop
// early, and the customer is left looking at the same wrong number.
export const hasBalanceIncreased = (
  baseline: ReadonlyMap<string, number>,
  current: ReadonlyMap<string, number>,
): boolean => {
  for (const [key, amount] of current) {
    const before = baseline.get(key)
    if (before !== undefined && amount > before) return true
  }
  return false
}

// A NEW id must appear — not "the set differs" — so a card deleted from the row
// menu while a checkout modal is open can't be read as a successful add.
export const hasNewCard = (
  baseline: readonly number[],
  current: readonly number[] | undefined,
): boolean => {
  if (!current) return false
  const known = new Set(baseline)
  return current.some((id) => !known.has(id))
}

interface PollParams {
  refresh: () => Promise<void>
  isSettled: () => boolean
  isCancelled?: () => boolean
  intervalMs: number
  maxAttempts: number
}

// Bounded await-loop rather than setInterval + a paired clearInterval/clearTimeout
// (the pollForPlanUpdate shape): it cannot drift, cannot double-fire, and the
// number of timers is capped by maxAttempts by construction. Never rejects — a
// transient refetch failure keeps polling, same posture as the existing helpers.
export const pollUntilSettled = async ({
  refresh,
  isSettled,
  isCancelled,
  intervalMs,
  maxAttempts,
}: PollParams): Promise<number> => {
  let attempts = 0
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) await delay(intervalMs)
    if (isCancelled?.()) return attempts
    attempts++
    try {
      await refresh()
    } catch {
      // Transient — keep polling.
    }
    if (isSettled()) return attempts
  }
  return attempts
}

const outcomeFor = (
  tracked: boolean,
  hasBaseline: boolean,
  settled: boolean,
): BillingRefreshOutcome => {
  if (!tracked) return 'not_tracked'
  if (!hasBaseline) return 'no_baseline'
  return settled ? 'updated' : 'timed_out'
}

// One in-flight run per account: a second payment supersedes the first rather
// than racing it. The newer baseline is the newer truth.
const activeRuns = new Map<number, { cancelled: boolean }>()

// Kick off a bounded, SILENT refresh of the billing cache. Never toasts — a poll
// outlives the modal that started it (by design: the customer closes the dialog
// and must still see the balance land), so an end-of-poll toast would fire on
// whatever screen they moved to.
export const refreshBillingAfterPayment = (
  client: QueryClient,
  accountId: number,
  options: BillingRefreshOptions = {},
): Promise<BillingRefreshResult> => {
  const {
    targets = ['balance', 'transactions'],
    intervalMs = BILLING_REFRESH_POLL_INTERVAL_MS,
    maxAttempts = BILLING_REFRESH_MAX_POLL_ATTEMPTS,
  } = options

  // getAccountId() returns 0 when the profile cache isn't warm (notably on the
  // post-redirect callback route). Invalidating ['billing-balance', 0] is pure
  // noise — those queries are `enabled: !!accountId`.
  if (!accountId) {
    return Promise.resolve({
      balance: 'not_tracked',
      cards: 'not_tracked',
      transactions: 'not_tracked',
      attempts: 0,
    })
  }

  const tracksBalance = targets.includes('balance')
  const tracksCards = targets.includes('cards')
  // Asked for AND observable. Requiring a transition on an unmounted query would
  // burn the whole budget waiting for a cache that cannot change — and waiting buys
  // nothing there, since the receipts list is off screen and refetches on next mount
  // (staleTime 0). Evaluated once, up front, alongside the baselines: a run should
  // not change its stop condition because the customer navigated mid-poll.
  const tracksTransactions =
    targets.includes('transactions') &&
    hasActiveQuery(client, billingQueryKeys().transactionsAll(accountId))
  // Snapshot BEFORE the first invalidate — this function is deliberately not
  // `async`, so the baseline is captured synchronously on the caller's tick while
  // the cache still holds the pre-payment values.
  const balanceBaseline = tracksBalance ? readBalanceAmounts(client, accountId) : new Map()
  const cardsBaseline = tracksCards ? readCardIds(client, accountId) : undefined
  const hasBalanceBaseline = tracksBalance && balanceBaseline.size > 0
  const transactionsBaseline = tracksTransactions
    ? readTransactionStatuses(client, accountId)
    : undefined
  const hasCardsBaseline = tracksCards && cardsBaseline !== undefined
  const hasTransactionsBaseline = tracksTransactions && transactionsBaseline !== undefined

  const previous = activeRuns.get(accountId)
  if (previous) previous.cancelled = true
  const run = { cancelled: false }
  activeRuns.set(accountId, run)

  const keys = billingQueryKeys()
  // Same blast radius as the code this replaces, so nothing can regress. Awaiting
  // is the refetch barrier: in v5 invalidateQueries resolves once the matching
  // ACTIVE queries have refetched, which is what removes the need for the blind
  // setTimeout the old copies used. refetchType stays at its default ('active'),
  // so a customer who navigates away mid-poll generates no traffic and simply
  // leaves the queries stale for the next mount.
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: keys.balanceAll(accountId) }),
      client.invalidateQueries({ queryKey: keys.transactionsAll(accountId) }),
      client.invalidateQueries({ queryKey: keys.cards(accountId) }),
    ])
  }

  // One predicate per target, read fresh from the cache each time. Kept separate
  // so the stop condition and the reported outcome are derived from the SAME
  // evidence without either re-spelling it.
  const balanceHasMoved = (): boolean =>
    hasBalanceBaseline &&
    hasBalanceIncreased(balanceBaseline, readBalanceAmounts(client, accountId))
  const cardHasAppeared = (): boolean =>
    hasCardsBaseline && hasNewCard(cardsBaseline, readCardIds(client, accountId))
  const transactionHasSettled = (): boolean =>
    hasTransactionsBaseline &&
    hasTransactionSettled(
      transactionsBaseline ?? new Map(),
      readTransactionStatuses(client, accountId) ?? new Map(),
    )

  // Stopping needs EVERY tracked target to have moved — this is an AND, and
  // deliberately so. Reporting is per-target (see below); conflating the two is
  // what made a moved balance report as timed out.
  const isSettled = (): boolean => {
    // A target that was asked for but has NO baseline cannot be detected — a first
    // fetch is indistinguishable from a real change. Never let a sibling target end
    // the run on its behalf though: spend the whole budget as a staggered refresh,
    // which is what eventually pulls the late value into the cache. Stopping early
    // is how "wait for the card I just added" silently became "wait for the balance".
    if (tracksBalance && !hasBalanceBaseline) return false
    if (tracksCards && !hasCardsBaseline) return false
    if (tracksTransactions && !hasTransactionsBaseline) return false
    if (hasBalanceBaseline && !balanceHasMoved()) return false
    if (hasCardsBaseline && !cardHasAppeared()) return false
    if (hasTransactionsBaseline && !transactionHasSettled()) return false
    // Every tracked target has a baseline and every one of them moved. With nothing
    // tracked at all this is blind mode: false, so the whole budget is spent as a
    // staggered refresh rather than stopping on the first fetch that lands.
    return hasBalanceBaseline || hasCardsBaseline || hasTransactionsBaseline
  }

  return pollUntilSettled({
    refresh,
    isSettled,
    isCancelled: () => run.cancelled,
    intervalMs,
    maxAttempts,
  }).then((attempts) => {
    if (activeRuns.get(accountId) === run) activeRuns.delete(accountId)
    // Each target reports on its OWN evidence. Feeding the AND from isSettled()
    // into both meant a run where the balance moved but no card ever appeared
    // (an unticked save box) called the balance 'timed_out' — a misleading answer
    // for the first caller that branches on it. (PR #3636 review, priyanshu.)
    return {
      balance: outcomeFor(tracksBalance, hasBalanceBaseline, balanceHasMoved()),
      cards: outcomeFor(tracksCards, hasCardsBaseline, cardHasAppeared()),
      transactions: outcomeFor(
        tracksTransactions,
        hasTransactionsBaseline,
        transactionHasSettled(),
      ),
      attempts,
    }
  })
}
